import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { ApiClientService } from './api-client.service';
import { Business } from '@reshiftdev/franify-assets-library';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiClient = inject<ApiClientService>(ApiClientService);

  private makeApiCall<T>(
    endpoint: string,
    payload: { [key: string]: string | string[] | boolean },
  ): Observable<{ msg: string; data: T }> {
    const uid: string = '685321c9851058e1909643ad';

    return this.apiClient
      .apiCall<{
        msg: string;
        data: T;
      }>(`${endpoint}?uid=${uid}`, payload)
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
      );
  }

  /**
   * Retrieves invite details by providing a valid invite_code for the admin dashboard platform.
   * This endpoint is used to verify invitation status and available 2FA methods.
   *
   * @param inviteCode string
   * @param platform 'admin_dashboard' | 'mobile_app' | 'demo'
   */
  inviteValidation(
    inviteCode: string,
    platform: 'admin_dashboard' | 'mobile_app' | 'demo',
  ): Observable<{
    msg: string;
    data: {
      user_name: string;
      email: string;
      first_name: string;
      last_name: string;
      platform: string;
      login: boolean;
      '2fa': boolean;
      options: {
        email: string;
        phone: string;
        google_auth: string;
      };
    };
  }> {
    return this.makeApiCall('auth/invite-validation', {
      invite_code: inviteCode,
      platform,
    });
  }

  /**
   * Sends a 2FA token to the invited user via the selected method (email or phone),
   * for the Admin Dashboard invite verification process.
   *
   * @param method 'email' | 'phone'
   * @param inviteCode string
   * @param value string
   * @param countryCode string
   */
  send2FACode(
    method: string,
    inviteCode: string,
    value: string,
    countryCode?: string,
  ): Observable<
    string | { otpauthUrl: string; qrCodeUrl: string; tempSecret: string }
  > {
    return this.makeApiCall<string>('auth/send-2fa-code', {
      method,
      invite_code: inviteCode,
      value,
      ...(countryCode && { country_code: countryCode }),
    }).pipe(map((value) => value.data));
  }

  invite2FAVerification(
    method: string,
    invite_code: string,
    password: string,
    first_name: string,
    last_name: string,
    token: string | null = null,
    email_check: boolean = false,
  ): Observable<any> {
    let payload: any = {
      method,
      invite_code,
      password,
      confirm_password: password,
      first_name,
      last_name,
    };

    if (method !== 'email') {
      payload['token'] = token;
    }

    if (email_check) {
      payload['email_check'] = email_check;
    }

    return this.makeApiCall<any>('auth/invite-2fa-verification', payload).pipe(
      map((value) => value.data),
    );
  }

  /**
   * Allows a user to automatically log in using a valid access_token, uid, and device_id.
   * If the token is valid, session tokens and user/business data are returned.
   *
   * @param uid string
   */
  autoLogin(
    uid: string,
    access_token: string,
  ): Observable<{
    access_token: string;
    business: Business[];
    login: boolean;
    refresh_token: string;
    uid: string;
    user_data: {
      email: string;
      first_name: string;
      last_name: string;
      phone: string | null;
    };
  }> {
    return this.makeApiCall<{
      access_token: string;
      business: Business[];
      login: boolean;
      refresh_token: string;
      uid: string;
      user_data: {
        email: string;
        first_name: string;
        last_name: string;
        phone: string | null;
      };
    }>('auth/auto-login', {
      uid,
      access_token,
    }).pipe(map((value) => value.data));
  }

  /**
   * Logs in a user using email and password for the admin dashboard.
   * If 2FA is enabled and not yet verified, a partial response is returned.
   * If credentials and session are valid, full session data is returned.
   *
   * @param credentials email: string, password: string
   */
  login(credentials: { email: string; password: string }): Observable<{
    '2fa': boolean;
    access_token: string;
    login: boolean;
    options: { email: string; google_auth: string; phone: string };
    uid: string;
    business: Business[];
  }> {
    return this.makeApiCall<{
      '2fa': boolean;
      access_token: string;
      login: boolean;
      options: { email: string; google_auth: string; phone: string };
      uid: string;
      business: Business[];
    }>('auth/login', {
      email: credentials.email,
      password: credentials.password,
    }).pipe(map((value) => value.data));
  }

  /**
   * Sends a new 2FA token to the user via the specified method (e.g., email or phone) for admin dashboard logins.
   *
   * @param uid string
   * @param method 'email' | 'phone' | 'google_auth'
   */
  send2FAToken(uid: string, method: string): Observable<string> {
    return this.makeApiCall<string>('auth/send-2fa-token', {
      uid,
      method,
    }).pipe(map((value) => value.data));
  }

  /**
   * Verifies a 2FA token (e.g., email or phone passcode) for a dashboard user after login.
   * Returns session tokens on success.
   *
   * @param uid string
   * @param token string
   * @param method 'email' | 'phone' | 'google_auth'
   */
  verify2FAToken(
    uid: string,
    token: string,
    method: string,
  ): Observable<{
    access_token: string;
    login: boolean;
    refresh_token: string;
    uid: string;
    user_data: {
      email: string;
      phone: string;
      first_name: string;
      last_name: string;
    };
    business: {
      business_id: string;
      business_name: string;
      role_id: string;
      role: string;
      services: {
        advertising: {
          meta: { name: string };
          google_search: { name: string };
          performance_max: { name: string };
        };
        dynamic_report: { name: string };
        posting: { name: string };
      };
    }[];
  }> {
    return this.makeApiCall<{
      access_token: string;
      login: boolean;
      refresh_token: string;
      uid: string;
      user_data: {
        email: string;
        phone: string;
        first_name: string;
        last_name: string;
      };
      business: {
        business_id: string;
        business_name: string;
        role_id: string;
        role: string;
        services: {
          advertising: {
            meta: { name: string };
            google_search: { name: string };
            performance_max: { name: string };
          };
          dynamic_report: { name: string };
          posting: { name: string };
        };
      }[];
    }>('auth/verify-2fa-token', {
      uid,
      token,
      method,
    }).pipe(map((value) => value.data));
  }

  /**
   * Sends a password reset link to the provided email address if it exists in the dashboard system.
   *
   * @param email string
   */
  forgotPaassword(email: string): Observable<string> {
    return this.makeApiCall<string>('auth/forgot-password', { email }).pipe(
      map((value) => value.data),
    );
  }

  /**
   * Retrieves user information associated with the password reset token for the dashboard platform.
   *
   * @param token string
   */
  getResetPasswordData(
    token: string,
  ): Observable<{ email: string; platform: string }> {
    return this.makeApiCall<{ email: string; platform: string }>(
      'auth/get-reset-password-data',
      { token },
    ).pipe(map((value) => value.data));
  }

  /**
   * Updates the user's password using the provided reset token and new password. May require 2FA verification.
   *
   * @param resetId string
   * @param email string
   * @param password string
   * @param confirmPassword string
   */
  resetPassword(
    resetId: string,
    email: string,
    password: string,
    confirmPassword: string,
  ): Observable<{
    '2fa': boolean;
    login: boolean;
    options: { email: string; google_auth: string; phone: string };
    uid: string;
  }> {
    return this.makeApiCall<{
      '2fa': boolean;
      login: boolean;
      options: { email: string; google_auth: string; phone: string };
      uid: string;
    }>('auth/reset-password', {
      token: resetId,
      email,
      password,
      confirm_password: confirmPassword,
    }).pipe(map((value) => value.data));
  }

  selectBusinessId(businessId: string): void {
    let currentBusinessId = localStorage.getItem('business_id');

    if (currentBusinessId !== businessId) {
      localStorage.setItem('business_id', businessId);
      // window.location.reload();
    }
  }
}
